import { useParams } from "react-router";
import { useI18n } from "~/i18n";
import { resolveIpAssets } from "~/theme/resolve-theme";
import { SECTION_TOP_DIVIDER } from "~/components/sections/section-top-divider";
import { SectionHeading } from "~/components/sections/section-heading";
import type { NewsListItem } from "@laftel-ip-originals/notion/contracts";

interface NewsListProps {
  items: NewsListItem[];
  isLoading?: boolean;
  onNewsClick?: (item: NewsListItem) => void;
}

export function NewsList({ items, isLoading = false, onNewsClick }: NewsListProps) {
  const { locale, t } = useI18n();
  const { slug = "" } = useParams<{ slug: string }>();
  const assets = resolveIpAssets(slug);

  return (
    <section id="news" className={`news relative z-[2] bg-white py-[60px] ${SECTION_TOP_DIVIDER}`}>
      <div className="max-w-full mx-auto px-[clamp(24px,5vw,120px)]">
        <SectionHeading imgSrc={assets.sectionTitles.news} alt="News" />

        {isLoading ? (
          <ul className="mx-auto max-w-[760px] list-none">
            {Array.from({ length: 4 }).map((_, index) => (
              <li
                key={index}
                className={`border-b border-[rgba(var(--ip-primary-glow),0.12)] ${index === 0 ? "border-t" : ""}`}
              >
                <div className="flex items-center gap-5 px-3 py-[22px] max-md:flex-col max-md:items-start max-md:gap-2 max-md:py-4">
                  <div className="contents max-md:flex max-md:items-center max-md:gap-3">
                    <div className="skeleton h-[18px] w-[64px] rounded-[2px] max-md:w-[54px]" />
                    <div className="skeleton h-[16px] w-[90px] max-md:w-[72px]" />
                  </div>
                  <div className="skeleton h-[16px] flex-1 rounded-[4px]" />
                </div>
              </li>
            ))}
          </ul>
        ) : items.length === 0 ? (
          <p className="text-center text-sm text-[var(--ip-text-muted)]">{t.news.noNews}</p>
        ) : (
          <ul className="news__list mx-auto max-w-[760px] list-none">
            {items.map((item, i) => (
              <li
                key={item.id}
                className={`news__item border-b border-[rgba(var(--ip-primary-glow),0.12)] ${i === 0 ? "border-t" : ""}`}
              >
                <button
                  onClick={() => onNewsClick?.(item)}
                  className="news__link group flex w-full items-center gap-5 py-[22px] px-3 text-left transition-colors hover:opacity-70 max-md:flex-col max-md:items-start max-md:gap-2 max-md:py-4"
                >
                  <div className="contents max-md:flex max-md:items-center max-md:gap-3">
                    <span className="news__category w-[64px] shrink-0 rounded-[2px] bg-[var(--ip-primary)] py-[3px] text-center text-[10px] font-medium tracking-[0.12em] text-white max-md:w-auto max-md:px-1.5 max-md:py-px max-md:leading-tight">
                      {item.category[locale]}
                    </span>
                    <span className="news__date w-[90px] shrink-0 whitespace-nowrap text-center font-['Barlow_Condensed',sans-serif] text-[15px] tracking-[0.02em] text-[var(--ip-text-muted)] font-medium max-md:w-auto max-md:text-[13px]">
                      {item.date}
                    </span>
                  </div>
                  <span className="news__title flex-1 truncate text-sm font-medium leading-[1.6] text-[var(--ip-text)] max-md:w-full">
                    {item.isPinned && (
                      <span className="mr-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#eee] -mt-px align-middle">
                        <svg
                          className="w-3 h-3 text-[#999] rotate-45"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2z" />
                        </svg>
                      </span>
                    )}
                    {item.title[locale] ?? ""}
                  </span>
                  <span className="news__arrow ml-auto shrink-0 text-sm text-[rgba(var(--ip-primary-glow),0.5)] transition-all group-hover:translate-x-[5px] group-hover:text-[var(--ip-primary)] max-md:hidden">
                    →
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
